import { map } from 'rxjs/operators';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Audit } from '../../models/entities/audit';
import { Injectable } from '@angular/core';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { OrganizationPendingReview } from '../../components/admin-layout/organizations/organizations.component';
import { GtmService } from '../gtm.service';
import { OrganizationReviewActions } from '../../enums/organization-review-actions.enum';
import environment from '../../../environments/environment';
import { Organizations } from '../../components/shared/http/request-interfaces';
import { PaginationMeta } from '../../models/entities/pagination';

export type GeographicManagementType = 'all' | 'pmc' | 'self';

export interface GeographicOrgFilters
{
    state?: string | null;
    city?: string | null;
    zip?: string | null;
    managementType?: GeographicManagementType;
    radiusMiles?: number | null;
    radiusZip?: string | null;
    radiusCity?: string | null;
    radiusState?: string | null;
    unitsCountEq?: number | null;
    unitsCountGt?: number | null;
    unitsCountLt?: number | null;
    page?: number;
    perPage?: number;
    sortBy?: 'name' | 'city' | 'state' | 'zip' | 'adminName' | 'unitCount' | 'customerSince' | 'csatScore';
    sortDir?: 'asc' | 'desc';
}

export interface GeographicOrgRow
{
    id: number;
    name: string;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    managementType: 'pmc' | 'self';
    pmcId: number | null;
    pmcName: string | null;
    unitCount: number;
    latitude: number | null;
    longitude: number | null;
    csatScore: number | null;
    adminName: string | null;
    adminEmail: string | null;
    adminMembershipId: number | null;
    customerSince: string | null;
}

export interface GeographicOrgsBreakdown
{
    all: number;
    pmc: number;
    self: number;
}

export interface GeographicOrgsAggregates
{
    totalUnits: number;
    avgUnitCount: number;
    avgCsatScore: number | null;
}

export interface GeographicOrgsResponse
{
    count: number;
    page: number;
    perPage: number;
    totalPages: number;
    organizations: GeographicOrgRow[];
    breakdown: GeographicOrgsBreakdown;
    aggregates: GeographicOrgsAggregates;
}

export interface ParsedGeographicQuery
{
    state: string | null;
    city: string | null;
    zip: string | null;
    radiusMiles: number | null;
    radiusZip: string | null;
    radiusCity: string | null;
    radiusState: string | null;
    unitsCountEq: number | null;
    unitsCountGt: number | null;
    unitsCountLt: number | null;
}

export interface ParsedGeographicQueryResponse
{
    filters: ParsedGeographicQuery;
}

export interface GeographicMapPointOrg
{
    id: number;
    name: string;
    managementType: 'pmc' | 'self';
}

export interface GeographicMapPoint
{
    zip: string;
    latitude: number;
    longitude: number;
    count: number;
}

export interface GeographicMapPointsResponse
{
    points: GeographicMapPoint[];
}

export interface GeographicClusterDetailResponse
{
    zip: string;
    organizations: GeographicMapPointOrg[];
}

export interface AuditHistoryResponse
{
    record: any;
    audits: any[];
    relatedRecords: {
        [key: string]: {
            className: string;
            id: number;
        }[]
    }[];
}

export interface AuditHistory
{
    record: any;
    audits: Audit[];
    relatedRecords: {
        [key: string]: {
            className: string;
            id: number;
        }[]
    }[];
}

@Injectable({
    providedIn: 'root',
})
export class AdminService
{
    constructor(
            private _http: ApplicationHttpClient,
            private _gtm: GtmService,
    ) {
    }

    public seed() {
        let url = '/rainforest/seed/0';
        if(environment.LegFiCoreApi.indexOf('qa') !== -1) {
            url = '/rainforest/seed/1';
        }
        return this._http.post(Routes.MakeLegFiCoreUrl(url), JSON.stringify({
            callback_type: 'before_run'
        }));
    }


    /**
     * @param {number} id
     * @returns {Observable<Object>}
     */
    public impersonateUser(id: string): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ImpersonateUser() + '?id=' + id,
        );

        return this._http.get(url);
    }

    /**
     * @returns {Observable<Object>}
     */
    public restore(): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.RestoreSuperAdmin(),
        );

        return this._http.get(url);
    }

    public getAuditHistory(model: string, id: number): Observable<AuditHistory> {
        const url: string = Routes.MakeLegFiCoreUrl('/audits/' + model + '/' + id);
        return this._http.get(url).pipe(map((res: AuditHistoryResponse) => {
            return {
                record: res.record,
                audits: res.audits.map(a => new Audit(a)),
                relatedRecords: res.relatedRecords,
            };
        }));
    }

    public getAudits(model: string): Observable<Audit[]> {
        const url: string = Routes.MakeLegFiCoreUrl('/audits?model=' + model);
        return this._http.get(url).pipe(map((res: Object[]) => {
            return res.map(a => new Audit(a));
        }));
    }

    public getPaginatedAuditHistory(
        model: string,
        id: number,
        page: number = 1,
        perPage: number = 15,
        sortBy: string = 'created_at',
        sortDir: 'asc' | 'desc' = 'desc',
        eventTypes?: string[]
    ): Observable<{ data: Audit[], meta: PaginationMeta }> {
        const params: any = {
            page,
            perPage,
            sortBy,
            sortDir,
            eventTypes
        };
        const url = Routes.MakeLegFiCoreUrl(`/audits/${model}/${id}/paginated`) + this._http.queryStringFromObject(params);

        return this._http.get(url).pipe(map((res: any) => ({
            data: res.data.map((a: any) => new Audit(a)),
            meta: new PaginationMeta(res.meta)
        })));
    }

    /**
     * @returns {Observable<Object>}
     */
    public getSubscriptionsBreakdown(): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Admin +
                Routes.LegFiCore.SubscriptionsBreakdown,
        );

        return this._http.get(url);
    }

    /**
     * @returns {Observable<Object>}
     */
    public getSubscriptionsSummary(): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Admin +
                Routes.LegFiCore.SubscriptionsSummary,
        );

        return this._http.get(url);
    }

    public getOrgsPendingReview(): Observable<OrganizationPendingReview[]> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationsPendingReview);
        return this._http.get(url);
    }

    public getOrganizationStates(): Observable<string[]> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AdminOrganizationStates);
        return this._http.get(url);
    }

    public getOrganizationsByGeography(filters: GeographicOrgFilters): Observable<GeographicOrgsResponse> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AdminOrganizationsByGeography)
            + this._http.queryStringFromObject(this._cleanGeographicFilters(filters));
        return this._http.get(url);
    }

    public exportOrganizationsByGeography(filters: GeographicOrgFilters): Observable<Blob> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AdminOrganizationsByGeographyExport)
            + this._http.queryStringFromObject(this._cleanGeographicFilters(filters));
        return this._http.get(url, { responseType: 'blob' });
    }

    public getOrganizationsByGeographyMapPoints(filters: GeographicOrgFilters): Observable<GeographicMapPointsResponse> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AdminOrganizationsByGeographyMapPoints)
            + this._http.queryStringFromObject(this._cleanGeographicFilters(filters));
        return this._http.get(url);
    }

    public getOrganizationsByGeographyClusterDetail(
        filters: GeographicOrgFilters,
        zip: string,
    ): Observable<GeographicClusterDetailResponse> {
        // Override the active filter `zip` with the clicked pin's zip so the response
        // narrows to that single cluster regardless of what the parsed query set.
        const params = { ...this._cleanGeographicFilters(filters), zip };
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AdminOrganizationsByGeographyClusterDetail)
            + this._http.queryStringFromObject(params);
        return this._http.get(url);
    }

    public parseGeographicQuery(query: string): Observable<ParsedGeographicQueryResponse> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AdminOrganizationsByGeographyParseQuery);
        return this._http.post(url, JSON.stringify({ query }));
    }

    private _cleanGeographicFilters(filters: GeographicOrgFilters): { [key: string]: any } {
        const cleaned: { [key: string]: any } = {};
        Object.entries(filters).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                return;
            }
            cleaned[key] = value;
        });
        return cleaned;
    }

    public reviewOrganization(organizationId: number, action: OrganizationReviewActions) {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationsPendingReview) + '/' + organizationId + '/' + action;
        return this._http.post(url, JSON.stringify({})).pipe(
                map((res) => {
                            this._gtm.reviewOrganization({
                                orgId: organizationId,
                                action,
                            });
                            return res;
                        },
                ));
    }

    /**
     * @returns {Observable<Object>}
     */
    public saveOrganizationRates(
            organizationId: number,
            rates: Organizations.SaveOrganizationRatesRequest,
    ): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationRates(organizationId));

        return this._http.post(url, JSON.stringify(rates));
    }

    /**
     * @param {{}} options
     * @returns {Observable<Object>}
     */
    public getOrganizationPayments(options?: { [key: string]: any }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const queryParams = [];
        let isPagingPresent: boolean = false;
        for (const key in options) {
            if (options.hasOwnProperty(key)) {
                queryParams.push(key + '=' + options[key]);
                isPagingPresent = isPagingPresent || (key === 'paging');
            }
        }
        if (!isPagingPresent) {
            queryParams.push('paging=true');
        }
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Admin +
                Routes.LegFiCore.PaymentGroups + '/organization-payments',
        ) + (queryParams.length > 0 ? '?' + queryParams.join('&') : '');

        return this._http.get(url);
    }

    public getMasterOrg(): Observable<{ orgId: number; }> {
        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminMasterOrg(),
        );

        return this._http.get(url);
    }

    public addOrgCredit(orgId, amount: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminCreditOrg(),
        );
        return this._http.post(url, JSON.stringify({
            orgId: orgId,
            amount: amount,
        }));
    }

    public processOrgPayment(orgId) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminProcessOrgPayment(),
        );
        return this._http.post(url, JSON.stringify({
            orgId: orgId,
        }));
    }
}
